import moment from 'moment-timezone';
import {
  formatDateWithDayAndMonth,
  get12HourFormat,
  truncateTime,
} from '@/utils/date';
import { Moment } from 'moment/moment';
import { useState } from 'react';

export const MINUTE_INTERVAL = 15;

/**
 * Verify if the date is after today, update to start of the day
 * @param reminderDate - The reminder date to be formatted
 * @returns {Date} - The minimum date to be set for the date picker
 */
const formatMinimumDate = (reminderDate: Date | Moment): Date => {
  const now = moment();
  const reminderMoment = moment(reminderDate);
  if (reminderMoment.isAfter(now)) {
    const newMinumumDate = reminderMoment.toDate();
    newMinumumDate.setHours(0, 0, 0, 0);
    return newMinumumDate;
  } else {
    // else - set minimum date as now
    return now.toDate();
  }
};

/**
 * Check if the first time is after the second time.
 * @param time1 (Date) - The first time to compare.
 * @param time2 (Date) - The second time to compare.
 * @returns boolean - Returns true if time1 is after time2, otherwise false.
 */
const isTimeAfter = (time1: Date, time2: Date): boolean => {
  return (
    time1.getHours() > time2.getHours() ||
    (time1.getHours() === time2.getHours() &&
      time1.getMinutes() > time2.getMinutes())
  );
};

export interface ReminderTimestamp {
  date: Date;
  time?: Date;
}

/**
 * Unified hook to handle content behavior for the date and time reminder selectors.
 * @param onChange
 * @param reminder
 */
export const useNoteReminderForm = (
  onChange: (reminder?: ReminderTimestamp) => void,
  reminder?: ReminderTimestamp,
) => {
  const [isDateReminderEnabled, setIsDateReminderEnabled] = useState(
    Boolean(reminder?.date),
  );
  const [isDateReminderCalendarVisible, setIsDateReminderCalendarVisible] =
    useState(false);
  const [isTimeReminderEnabled, setIsTimeReminderEnabled] = useState(
    Boolean(reminder?.time),
  );
  const [isTimeReminderModalVisible, setIsTimeReminderModalVisible] =
    useState(false);
  const [minimumDate, setMinimumDate] = useState(
    formatMinimumDate(reminder ? reminder.date : new Date()),
  );

  // Date Toggle Handlers
  const onDateChange = (date: Moment) => {
    const reminderDate = date.toDate();
    const currentTime = new Date();
    const reminderTime = new Date(reminderDate);
    let newReminderTime = reminder?.time;

    // validation to ensure time is valid on the selected date
    if (!newReminderTime) {
      newReminderTime = currentTime;
    }
    // if reminderDate (in UTC) is today (in local time)
    else if (reminderDate.getUTCDate() === currentTime.getDate()) {
      //  take max of current time and reminder time
      newReminderTime = isTimeAfter(newReminderTime, currentTime)
        ? newReminderTime
        : currentTime;
    }
    // else take reminder time -- do nothing
    // set reminder time to the selected date
    reminderTime.setHours(
      newReminderTime.getHours(),
      newReminderTime.getMinutes(),
      0,
      0,
    );
    onChange({
      date: date.toDate(),
      time: truncateTime(reminderTime, MINUTE_INTERVAL),
    });

    setMinimumDate(formatMinimumDate(date));
  };

  const onDateSwitchChange = (isDateOn: boolean) => {
    setIsDateReminderEnabled(isDateOn);
    setIsDateReminderCalendarVisible(isDateOn);
    if (!isDateOn) {
      onChange(undefined);
      setMinimumDate(new Date());
      setIsTimeReminderEnabled(false);
    }
  };

  const onClickDateSetting = () => {
    if (isDateReminderEnabled) {
      setIsDateReminderCalendarVisible(!isDateReminderCalendarVisible);
    }
  };

  // Time Toggle Handlers
  const onTimeChange = (time: Date) => {
    const reminderDate = reminder?.date ? reminder.date : new Date();
    const reminderTime = new Date(reminderDate);
    reminderTime.setHours(time.getHours(), time.getMinutes(), 0, 0);

    onChange({
      date: reminderDate,
      time: truncateTime(reminderTime, MINUTE_INTERVAL),
    });
    setIsTimeReminderModalVisible(false);
  };

  const onTimeSwitchChange = (isTimeOn: boolean) => {
    // If time toggle is turned on, it will collapse the calendar component
    if (isTimeOn) {
      setIsDateReminderCalendarVisible(false);
      if (!reminder) {
        // If no reminder exists, create a new one with the current date
        onChange({
          date: new Date(),
          time: truncateTime(new Date(), MINUTE_INTERVAL),
        });
      }
    }

    setIsTimeReminderEnabled(isTimeOn);
    setIsTimeReminderModalVisible(isTimeOn);

    // if date toggle is off, clear the reminder
    if (!isDateReminderEnabled) {
      onChange(undefined);
    }
  };

  const onClickTimeSetting = () => {
    if (isTimeReminderEnabled) {
      setIsTimeReminderModalVisible(!isTimeReminderModalVisible);
    }
  };

  // Prepare subtitles for toggles
  const dateToggleSubtitle =
    formatDateWithDayAndMonth(reminder?.date) || undefined;
  const timeToggleSubtitle = reminder?.time
    ? get12HourFormat(reminder.time.getHours(), reminder.time.getMinutes())
    : undefined;

  return {
    dateToggleSubtitle,
    isDateReminderCalendarVisible,
    isDateReminderEnabled,
    isTimeReminderEnabled,
    isTimeReminderModalVisible,
    minimumDate,
    onClickDateSetting,
    onClickTimeSetting,
    onDateChange,
    onDateSwitchChange,
    onTimeChange,
    onTimeSwitchChange,
    timeToggleSubtitle,
  };
};
